import React, { useState, useEffect } from 'react';
import { Bell, Mail, Phone, Code, Copy, Check, BellRing } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';

import { API_BASE as API } from '@/utils/api';

export const SubscribeModal = ({ isOpen, onClose, notificationsEnabled, setNotificationsEnabled }) => {
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState({ email: '', sms: '', webhook: '' });
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen) {
      axios.get(`${API}/api/widget/embed-code`).then(res => setEmbedCode(res.data.embed_code)).catch(() => {});
    }
  }, [isOpen]);

  const handlePushEnable = async () => {
    if (!('Notification' in window)) {
      alert('Push notifications not supported');
      return;
    }
    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      setNotificationsEnabled(true);
      localStorage.setItem('pushNotificationsEnabled', 'true');
    }
  };

  const handleEmailSubscribe = async () => {
    if (!email) return;
    try {
      await axios.post(`${API}/api/subscribe/email`, { email });
      setSubscribeStatus(s => ({ ...s, email: 'Subscribed!' }));
    } catch {
      setSubscribeStatus(s => ({ ...s, email: 'Error' }));
    }
  };

  const handleSMSSubscribe = async () => {
    if (!phone) return;
    try {
      await axios.post(`${API}/api/subscribe/sms`, { phone });
      setSubscribeStatus(s => ({ ...s, sms: 'Subscribed!' }));
    } catch {
      setSubscribeStatus(s => ({ ...s, sms: 'Error' }));
    }
  };

  const handleWebhookSubscribe = async () => {
    if (!webhookUrl) return;
    try {
      await axios.post(`${API}/api/subscribe/webhook`, { url: webhookUrl });
      setSubscribeStatus(s => ({ ...s, webhook: 'Registered!' }));
    } catch {
      setSubscribeStatus(s => ({ ...s, webhook: 'Error' }));
    }
  };

  const copyEmbedCode = async () => {
    await navigator.clipboard.writeText(embedCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="apple-card border-0 max-w-md max-h-[85vh] overflow-y-auto modal-mobile-optimized modal-glow">
        <DialogHeader>
          <DialogTitle className="apple-headline flex items-center gap-2">
            <Bell size={20} />
            Stay Updated
          </DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="push" className="mt-4">
          <TabsList className="grid w-full grid-cols-4 gap-1 p-1 rounded-xl" style={{ background: 'var(--apple-bg-secondary)' }}>
            {[
              { value: 'push', icon: BellRing, label: 'Push' },
              { value: 'email', icon: Mail, label: 'Email' },
              { value: 'sms', icon: Phone, label: 'SMS' },
              { value: 'embed', icon: Code, label: 'Embed' },
            ].map(({ value, icon: Icon, label }) => (
              <TabsTrigger key={value} value={value} className="rounded-lg text-xs data-[state=active]:bg-white/10">
                <Icon size={14} className="mr-1" />{label}
              </TabsTrigger>
            ))}
          </TabsList>
          
          <TabsContent value="push" className="mt-4">
            <button onClick={handlePushEnable} disabled={notificationsEnabled} className={`apple-button w-full ${notificationsEnabled ? '' : 'apple-button-primary'}`} data-testid="enable-push-btn">
              {notificationsEnabled ? '✓ Notifications Enabled' : 'Enable Push Notifications'}
            </button>
          </TabsContent>
          
          <TabsContent value="email" className="mt-4 space-y-3">
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="your@email.com" className="apple-input w-full" data-testid="email-input" />
            <button onClick={handleEmailSubscribe} className="apple-button apple-button-primary w-full" data-testid="email-submit-btn">
              {subscribeStatus.email || 'Subscribe to Daily Digest'}
            </button>
          </TabsContent>
          
          <TabsContent value="sms" className="mt-4 space-y-3">
            <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 234 567 8900" className="apple-input w-full" data-testid="phone-input" />
            <button onClick={handleSMSSubscribe} className="apple-button apple-button-primary w-full" data-testid="sms-submit-btn">
              {subscribeStatus.sms || 'Get SMS Alerts'}
            </button>
          </TabsContent>
          
          <TabsContent value="embed" className="mt-4 space-y-3">
            <pre className="p-3 rounded-xl text-xs overflow-auto" style={{ background: 'var(--apple-bg-secondary)', color: 'var(--apple-text-secondary)' }}>
              {embedCode || 'Loading...'}
            </pre>
            <button onClick={copyEmbedCode} className="apple-button apple-button-primary w-full" data-testid="copy-embed-btn">
              {copied ? <><Check size={14} className="inline mr-2" />Copied!</> : <><Copy size={14} className="inline mr-2" />Copy Embed Code</>}
            </button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
